import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ArrowLeft, ChevronLeft, ChevronRight, MessageSquare, Calendar, Tag, Images } from 'lucide-react';
import useMeta from '../hooks/useMeta';

// Helper to optimize images using Cloudflare resizing on the fly
const getResizedImageUrl = (url, width = 800, quality = 80) => {
  if (!url || !url.startsWith('https://assets.auxodev.dk/')) return url;
  if (url.includes('/cdn-cgi/image/')) return url;
  return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/`);
};

const ProjectsPage = () => {
  useMeta(
    'Udførte Projekter & Referencer | SL Flise- og murerforretning',
    'Se billeder af vores professionelle flise- og murerarbejde i Tønder og omegn. Vi leverer præcisionsarbejde inden for badeværelser, klinker og udendørs fliser.'
  );

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState('Alle');
  const [categories, setCategories] = useState(['Alle']);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/projects');
        const data = await response.json();
        setProjects(data);

        // Extract unique categories
        const uniqueCategories = ['Alle', ...new Set(data.map(p => p.category).filter(Boolean))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedProject) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedProject]);

  // Preload adjacent images in the background for instant switching
  useEffect(() => {
    if (!selectedProject || !selectedProject.image || selectedProject.image.length <= 1) return;

    const preloadIndices = [
      (currentImageIndex + 1) % selectedProject.image.length,
      (currentImageIndex - 1 + selectedProject.image.length) % selectedProject.image.length
    ];

    preloadIndices.forEach((idx) => {
      const imgUrl = getResizedImageUrl(selectedProject.image[idx], 1200);
      const img = new Image();
      img.src = imgUrl;
    });
  }, [selectedProject, currentImageIndex]);

  // Prefetch first image of a project on hover
  const handleProjectHover = (project) => {
    if (!project || !project.image || project.image.length === 0) return;
    const imgUrl = getResizedImageUrl(project.image[0], 1200);
    const img = new Image();
    img.src = imgUrl;
  };

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedProject) return;

      if (e.key === 'Escape') {
        setSelectedProject(null);
      } else if (e.key === 'ArrowLeft') {
        setCurrentImageIndex((prev) =>
          prev === 0 ? selectedProject.image.length - 1 : prev - 1
        );
      } else if (e.key === 'ArrowRight') {
        setCurrentImageIndex((prev) =>
          prev === selectedProject.image.length - 1 ? 0 : prev + 1
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedProject]);

  const handleOpenProject = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const handleCloseModal = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === 0 ? selectedProject.image.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === selectedProject.image.length - 1 ? 0 : prev + 1
    );
  };

  const filteredProjects = activeFilter === 'Alle'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  return (
    <div className="min-h-screen bg-white text-slate-800 pt-24 pb-16">
      {/* Hero Header */}
      <section className="relative py-20 overflow-hidden border-b border-slate-100 bg-slate-50">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.05),transparent_50%)]"></div>
        <div className="absolute -left-40 -top-40 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center text-slate-500 hover:text-orange-500 transition-colors mb-6 group text-sm font-medium"
          >
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Tilbage til forsiden
          </Link>
          
          <div className="max-w-3xl">
            <span className="text-orange-500 text-xs font-bold uppercase tracking-widest mb-3 block">
              Vores Håndværk
            </span>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 mb-6">
              Projekter & Referencer
            </h1>
            <p className="text-slate-600 text-lg sm:text-xl leading-relaxed">
              Her kan du gå i dybden med vores udførte arbejde. Vi lægger stor stolthed i hver eneste opgave, og vi går aldrig på kompromis med kvaliteten – uanset om det gælder eksklusive badeværelser, køkkener eller udendørs flisearbejde.
            </p>
          </div>
        </div>
      </section>

      {/* Projects Grid Section */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Category Filter Bar */}
        {categories.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-12 border-b border-slate-100 pb-6">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                  activeFilter === cat
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 border border-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="py-24 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-slate-500">Indlæser projekter...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-20 bg-slate-50 rounded-2xl border border-slate-100">
            <Images className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">Ingen projekter fundet</h3>
            <p className="text-slate-500">Der blev ikke fundet nogen projekter i denne kategori.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                onClick={() => handleOpenProject(project)}
                onMouseEnter={() => handleProjectHover(project)}
                className="group relative overflow-hidden rounded-2xl aspect-[4/5] bg-white border border-slate-100 cursor-pointer shadow-md hover:shadow-xl hover:shadow-orange-500/5 transition-all duration-500"
              >
                {/* Project Image */}
                <img
                  src={getResizedImageUrl(project.image[0], 600)}
                  alt={project.title.da}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                
                {/* Image Count Badge */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-slate-800 px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2 border border-slate-200 shadow-md">
                  <i className="fa-solid fa-images text-orange-500"></i>
                  <span>{project.image ? project.image.length : 1} billeder</span>
                </div>

                {/* Category Badge */}
                <div className="absolute top-4 left-4 bg-orange-500/90 backdrop-blur-sm text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md">
                  {project.category}
                </div>

                {/* Project Info */}
                <div className="absolute bottom-0 left-0 p-6 w-full bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent pt-16">
                  <h4 className="text-lg font-bold text-white group-hover:text-orange-400 transition-colors">
                    {project.title.da}
                  </h4>
                  <p className="text-slate-300 text-xs mt-1 flex items-center gap-1.5">
                    <i className="fa-solid fa-arrow-pointer text-orange-500/70"></i>
                    Klik for at se galleri
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-3xl p-8 md:p-12 border border-slate-200 relative overflow-hidden">
          <div className="absolute right-0 bottom-0 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl pointer-events-none"></div>
          
          <div className="max-w-2xl relative z-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              Har du et lignende projekt i tankerne?
            </h3>
            <p className="text-slate-600 mb-8 text-base sm:text-lg leading-relaxed">
              Vi står altid klar til at tage en uforpligtende snak om dine drømme og ønsker. Lad os hjælpe dig med at skabe det perfekte resultat med fokus på kvalitet og holdbarhed.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/#kontakt"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg shadow-orange-500/10 inline-flex items-center gap-2"
              >
                <i className="fa-solid fa-paper-plane"></i>
                Få et uforpligtende tilbud
              </Link>
              <a
                href="https://www.facebook.com/p/SL-Flise-og-murerforretning-100049126461160/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white hover:bg-slate-50 text-slate-800 font-bold py-3 px-6 rounded-xl transition-all border border-slate-200 inline-flex items-center gap-2"
              >
                Se mere på Facebook
                <ExternalLink className="h-4 w-4 text-orange-500" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-10 bg-slate-950/95 backdrop-blur-md overflow-y-auto">
          <div 
            className="relative max-w-4xl w-full bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 shadow-2xl flex flex-col my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 z-20 bg-slate-950/80 hover:bg-orange-500 text-white hover:text-white p-3 rounded-full transition-all border border-slate-800 focus:outline-none cursor-pointer"
              aria-label="Luk"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>

            {/* Gallery Viewer */}
            <div className="w-full bg-slate-950 flex flex-col justify-center relative group/viewer">
              {/* Main Active Image */}
              <div 
                className="relative aspect-[4/3] sm:aspect-[16/10] md:aspect-[16/9] flex items-center justify-center overflow-hidden"
              >
                <img
                  src={getResizedImageUrl(selectedProject.image[currentImageIndex], 1200)}
                  alt={`${selectedProject.title.da} - ${currentImageIndex + 1}`}
                  className="max-w-full max-h-[65vh] object-contain transition-all duration-300"
                />

                {/* Navigation Arrows on Main Image */}
                {selectedProject.image.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-4 bg-slate-900/80 hover:bg-orange-500 text-white p-3 rounded-full transition-all border border-slate-800 opacity-0 group-hover/viewer:opacity-100 focus:outline-none cursor-pointer"
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-4 bg-slate-900/80 hover:bg-orange-500 text-white p-3 rounded-full transition-all border border-slate-800 opacity-0 group-hover/viewer:opacity-100 focus:outline-none cursor-pointer"
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails Grid */}
              {selectedProject.image.length > 1 && (
                <div className="p-4 bg-slate-900/50 border-t border-slate-800/80 overflow-x-auto">
                  <div className="flex gap-2 justify-start sm:justify-center min-w-max">
                    {selectedProject.image.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all focus:outline-none shrink-0 cursor-pointer ${
                          idx === currentImageIndex 
                            ? 'border-orange-500 scale-105 shadow-md shadow-orange-500/20' 
                            : 'border-slate-800 hover:border-slate-600'
                        }`}
                      >
                        <img
                          src={getResizedImageUrl(img, 150)}
                          alt={`Miniature ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Bar: Project Info & Actions */}
            <div className="p-6 bg-slate-900 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <span className="text-orange-500 text-[10px] font-bold uppercase tracking-wider block mb-1">
                  {selectedProject.category}
                </span>
                <h3 className="text-xl font-bold text-white tracking-tight">
                  {selectedProject.title.da}
                </h3>
                <p className="text-slate-400 text-xs mt-1">
                  Billede {currentImageIndex + 1} af {selectedProject.image.length}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Link
                  to="/#kontakt"
                  onClick={handleCloseModal}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 px-5 rounded-xl transition-all text-sm flex items-center gap-2 shadow-lg shadow-orange-500/10"
                >
                  <i className="fa-solid fa-paper-plane"></i>
                  <span>Få et lignende tilbud</span>
                </Link>
                <button
                  onClick={handleCloseModal}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2.5 px-4 rounded-xl transition-all text-sm border border-slate-700 cursor-pointer"
                >
                  Luk
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
